import { useEffect, useMemo, useRef } from "react";
import { create } from "zustand";

import {
  commitLocalStorageItem,
  getLocalStorageItem,
  useLocalStorage,
} from "../hooks/useLocalStorage";
import { useThreadShells } from "../state/entities";
import { useNeucleosWorldStore } from "./worldStore";
import { allModeActive, localModeActive, type NeucleosWorldSelection } from "./worldSelection";
import { readWorldSeenCursor, stampWorldSeenCursor } from "./worldSeenCursorClient";
import {
  EMPTY_ACTOR_SEEN_CURSORS,
  resolveSeenCursors,
  type ActorSeenCursors,
} from "./worldSeenCursorResolve";
import {
  EMPTY_WORLD_SEEN_CURSORS,
  projectUnseenSettleRollups,
  stampSeenCursors,
  WORLD_SEEN_CURSORS_STORAGE_KEY,
  WorldSeenCursorsSchema,
  type SeenCursorDoor,
  type WorldUnseenSettleRollups,
} from "./worldUnseenSettle";

// NV5 — the live halves of unseen settle. Rollups read the RAW shell atom
// unfiltered (the WL1 stance: a World is a filter over threads, not a runtime
// boundary) so a settle in a backgrounded World can reach its door; the
// cursors ride the one schema'd localStorage doorway, so every mounted
// reader re-folds when a stamp lands.
//
// NV8 — the Actor-level half: each owning World's viewPrefs row carries the
// Actor's own cursor, adopted here per entitled World and merged into the
// device cursors most-recent-wins (worldSeenCursorResolve) before the fold
// runs. A door that refuses — stale grant, unreachable server — adopts
// nothing, and the device-local cursor carries alone.

interface ActorSeenCursorState {
  readonly byWorldKey: ActorSeenCursors;
  readonly adopt: (worldKey: string, seenCursorAt: number | null) => void;
}

/** In-memory adoption of the World doors' Actor cursors; never persisted here
 * (the World authority owns them — this is a mirror of reads, DL4). */
export const useActorSeenCursorStore = create<ActorSeenCursorState>((set) => ({
  byWorldKey: EMPTY_ACTOR_SEEN_CURSORS,
  adopt: (worldKey, seenCursorAt) =>
    set((state) => {
      // A typed null (never stamped) adopts nothing — absence stays absence.
      if (seenCursorAt === null) return state;
      const current = state.byWorldKey[worldKey];
      if (current !== undefined && current >= seenCursorAt) return state;
      return { byWorldKey: { ...state.byWorldKey, [worldKey]: seenCursorAt } };
    }),
}));

/**
 * Adopt each entitled World's Actor cursor through the grant door. Mounted
 * once by the always-mounted rail chrome, beside the stamp hook.
 */
export function useActorSeenCursorAdoption(): void {
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const worldKeysKey = useNeucleosWorldStore((state) =>
    state.worlds.map((world) => world.worldKey).join("\n"),
  );
  const adopt = useActorSeenCursorStore((state) => state.adopt);
  useEffect(() => {
    if (!signedIn || worldKeysKey.length === 0) return;
    let cancelled = false;
    for (const worldKey of worldKeysKey.split("\n")) {
      void readWorldSeenCursor(worldKey).then((outcome) => {
        if (!cancelled && outcome.ok) adopt(worldKey, outcome.seenCursorAt);
      });
    }
    return () => {
      cancelled = true;
    };
  }, [adopt, signedIn, worldKeysKey]);
}

/** Raw-stamp inputs: never the strip adapter's EFFECTIVE lifecycle fields. */
export function useWorldUnseenSettleRollups(): WorldUnseenSettleRollups {
  const shells = useThreadShells();
  const [device] = useLocalStorage(
    WORLD_SEEN_CURSORS_STORAGE_KEY,
    EMPTY_WORLD_SEEN_CURSORS,
    WorldSeenCursorsSchema,
  );
  const actor = useActorSeenCursorStore((state) => state.byWorldKey);
  return useMemo(
    () =>
      projectUnseenSettleRollups({
        threads: shells.map((shell) => ({
          worldKey: shell.worldKey ?? null,
          archivedAt: shell.archivedAt ?? null,
          settledAt: shell.settledAt ?? null,
        })),
        cursors: resolveSeenCursors(device, actor),
      }),
    [actor, device, shells],
  );
}

/**
 * The one door derivation (F04): signed-out or chosen Local is the Local
 * door; the ALL stance stands in no single door (an overview is not a visit,
 * so markers keep accruing); otherwise the active World.
 */
export function resolveSeenCursorDoor(state: {
  readonly signedIn: boolean;
  readonly worldCount: number;
  readonly selection: NeucleosWorldSelection | null;
  readonly activeWorldKey: string | null;
}): SeenCursorDoor | null {
  if (
    localModeActive({
      signedIn: state.signedIn,
      worldCount: state.worldCount,
      selection: state.selection,
    })
  ) {
    return { kind: "local" };
  }
  if (allModeActive(state.selection)) return null;
  return state.activeWorldKey === null ? null : { kind: "world", worldKey: state.activeWorldKey };
}

const doorKeyOf = (door: SeenCursorDoor | null): string | null =>
  door === null ? null : door.kind === "local" ? "local" : `world:${door.worldKey}`;

/**
 * Stamp the seen cursors on every active-door transition: the departed door
 * (its watch ends now) and the entered door (everything already settled here
 * is being looked at). Mount stamps the door the app woke up standing in.
 * Writes ride commitLocalStorageItem so mounted readers re-fold immediately.
 * NV8: World doors stamp BOTH custody tiers — the device cursor always, and
 * the owning World's viewPrefs row through the grant door when signed in; a
 * refusal leaves the device custody carrying alone.
 */
export function useWorldSeenCursorStamp(): void {
  const signedIn = useNeucleosWorldStore((state) => state.signedIn);
  const worldCount = useNeucleosWorldStore((state) => state.worlds.length);
  const selection = useNeucleosWorldStore((state) => state.selection);
  const activeWorldKey = useNeucleosWorldStore((state) => state.activeWorldKey);
  const loaded = useNeucleosWorldStore((state) => state.loaded);
  const door = loaded
    ? resolveSeenCursorDoor({ signedIn, worldCount, selection, activeWorldKey })
    : null;
  const doorKey = doorKeyOf(door);
  const doorRef = useRef<SeenCursorDoor | null>(null);
  doorRef.current = door;
  const previousRef = useRef<SeenCursorDoor | null>(null);
  useEffect(() => {
    const current = doorRef.current;
    const previous = previousRef.current;
    previousRef.current = current;
    const doors: SeenCursorDoor[] = [];
    if (previous !== null && doorKeyOf(previous) !== doorKey) doors.push(previous);
    if (current !== null) doors.push(current);
    if (doors.length === 0) return;
    const stored =
      getLocalStorageItem(WORLD_SEEN_CURSORS_STORAGE_KEY, WorldSeenCursorsSchema) ??
      EMPTY_WORLD_SEEN_CURSORS;
    commitLocalStorageItem(
      WORLD_SEEN_CURSORS_STORAGE_KEY,
      stampSeenCursors(stored, doors, new Date().toISOString()),
      WorldSeenCursorsSchema,
    );
    if (!useNeucleosWorldStore.getState().signedIn) return;
    const { adopt } = useActorSeenCursorStore.getState();
    for (const door of doors) {
      if (door.kind !== "world") continue;
      void stampWorldSeenCursor(door.worldKey).then((outcome) => {
        if (outcome.ok) adopt(door.worldKey, outcome.seenCursorAt);
      });
    }
  }, [doorKey]);
}
